import * as React from "react";
import { useEffect, useRef, useState } from 'react';
import { CallClient, CallAgent, LocalVideoStream, Call, RemoteParticipant } from '@azure/communication-calling';
import { AzureCommunicationTokenCredential } from '@azure/communication-common';
import { Alert, Avatar, CallEndIcon, CallIcon, CallVideoIcon, CallVideoOffIcon, ChatIcon, Checkbox, CloseIcon, ExclamationTriangleIcon, MicIcon, MicOffIcon, SettingsIcon, Tooltip, UserFriendsIcon } from "@fluentui/react-northstar";
import { Button, Loader, Popup } from "@fluentui/react-northstar";
import DeviceSelector from "./devices-settings";
import RemoteParticipantView from "./remote-participant";
import Participants from "./participants";
import './custom-call-screen.scss';
import CustomChat from "./custom-chat";

interface Props {
    token: string;
    userId: string;
    displayName: string;
    meetingLink: string;
    onCallStarted: () => void;
    onCallEnded: () => void;
    queueData: any;
    endpoint: string;
}

const CustomCallScreen: React.FC<Props> = ({ token, userId, displayName, meetingLink, onCallStarted, onCallEnded, queueData, endpoint }) => {
    const rawStreamRef = useRef<MediaStream>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawVideoWidth = useRef<number>();
    const drawVideoHeight = useRef<number>();
    const videoOffsetX = useRef<number>(0);
    const videoOffsetY = useRef<number>(0);
    const drawLoopRef = useRef<number>();
    const previewVideoRef = useRef<HTMLVideoElement | null>(null);
    const toggleTimeoutRef = useRef<NodeJS.Timeout | undefined>();

    const [call, setCall] = useState<Call | null>(null);
    const [callAgent, setCallAgent] = useState<CallAgent | null>(null);
    const [callState, setCallState] = useState<string | null>(null);
    const [isVideoOn, setVideoOn] = useState(false);
    const [isAudioOn, setAudioOn] = useState(true);
    const [deviceError, setDeviceError] = useState<string>();
    const [isAnonymousMode, setAnonymousMode] = useState(true);
    const [localStream, setLocalStream] = useState<LocalVideoStream | null>(null);
    const [remoteParticipants, setRemoteParticipants] = useState<RemoteParticipant[]>([]);
    const [inMeeting, setInMeeting] = useState(false);
    const [threadId, setThreadId] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState<string | null>(null);
    const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState<string | null>(null);
    const [callClient] = useState<CallClient>(new CallClient());
    const [showChatPanel, setShowChatPanel] = useState(false);
    const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);
    const registeredParticipants = useRef<WeakSet<RemoteParticipant>>(new WeakSet());

    const openSettings = () => setSettingsOpen(true);

    const stopStreamsAndAnimation = () => {
        if (drawLoopRef.current) {
            cancelAnimationFrame(drawLoopRef.current);
            drawLoopRef.current = undefined;
        }

        const clearStream = (el: HTMLVideoElement | null) => {
            if (el?.srcObject instanceof MediaStream) {
                el.srcObject.getTracks().forEach(track => track.stop());
                el.srcObject = null;
            }
        };

        clearStream(previewVideoRef.current);
        clearStream(videoRef.current);

        if (canvasRef.current) {
            const tracks = canvasRef.current.captureStream?.().getTracks?.();
            tracks?.forEach(track => track.stop());
        }

        canvasRef.current = null;
        previewVideoRef.current = null;
    };

    const toggleAnonymousMode = () => {
        if (toggleTimeoutRef.current) return;
        setAnonymousMode(prev => !prev);
        toggleTimeoutRef.current = setTimeout(() => {
            toggleTimeoutRef.current = undefined;
        }, 300);
    };

    useEffect(() => {
        const createAgent = async () => {
            const credential = new AzureCommunicationTokenCredential(token);
            const agent = await callClient.createCallAgent(credential, { displayName });
            setCallAgent(agent);
        };

        const createConstraints = async () => {
            try {
                rawStreamRef.current = await navigator.mediaDevices.getUserMedia({
                    video: { deviceId: selectedVideoDeviceId ?? undefined },
                    audio: { deviceId: selectedAudioDeviceId ?? undefined }
                });
            } catch (err: any) {
                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                    setDeviceError("Você bloqueou o acesso à câmera e/ou microfone.");
                    console.log("Permissão negada: o usuário bloqueou o acesso à câmera e/ou microfone.");
                } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                    setDeviceError("Nenhum dispositivo de câmera ou microfone foi encontrado.");
                    console.log("Nenhum dispositivo de câmera ou microfone foi encontrado.");
                } else if (err.name === 'NotReadableError') {
                    setDeviceError("Dispositivo de mídia ocupado: câmera ou microfone já está em uso.");
                    console.log("Dispositivo de mídia ocupado: câmera ou microfone já está em uso.");
                } else if (err.name === 'OverconstrainedError') {
                    setDeviceError("As configurações solicitadas (resolução ou dispositivo específico) não podem ser atendidas.");
                    console.log("As configurações solicitadas (resolução ou dispositivo específico) não podem ser atendidas.");
                } else {
                    setDeviceError(`Erro desconhecido ao acessar mídia: ${err}`);
                    console.log("Erro desconhecido ao acessar mídia:", err);
                }
            }
        }

        createAgent();
        createConstraints();
        

        return () => {
            callAgent?.dispose();
            stopStreamsAndAnimation();
        };
    }, []);

    useEffect(() => {
        const setup = async () => {
            stopStreamsAndAnimation();

            if (!isVideoOn) {
                if (localStream) {
                    try {
                        await call?.stopVideo(localStream);
                    } catch (e) {
                        console.warn("Erro ao parar stream de vídeo local:", e);
                    }
                    setLocalStream(null);
                }
                return;
            }

            rawStreamRef.current = await navigator.mediaDevices.getUserMedia({
                video: { deviceId: selectedVideoDeviceId ?? undefined },
                audio: { deviceId: selectedAudioDeviceId ?? undefined }
            });

            let finalStream: MediaStream;

            if (isAnonymousMode && isVideoOn) {
                const previewVideo = previewVideoRef.current ?? document.createElement('video');
                previewVideo.srcObject = rawStreamRef.current;
                previewVideo.autoplay = true;
                previewVideo.muted = true;

                await new Promise<void>((resolve) => {
                    previewVideo.onloadedmetadata = () => resolve();
                });

                await previewVideo.play();

                previewVideoRef.current = previewVideo;

                if (!canvasRef.current) {
                    const canvas = document.createElement('canvas');
                    const wrapperElement = document.getElementsByClassName("video-wrapper")[0];

                    //console.log(wrapperElement.clientWidth, wrapperElement.clientHeight)

                    // Parametrizar (calc de ratio)
                    canvas.width = 640;
                    canvas.height = 360;
                    canvasRef.current = canvas;
                }

                const videoAspect = previewVideo.videoWidth / previewVideo.videoHeight;
                const canvasAspect = canvasRef.current?.width / canvasRef.current?.height;

                drawVideoWidth.current = canvasRef.current?.width;
                drawVideoHeight.current = canvasRef.current?.height;

                if (videoAspect > canvasAspect) {
                    drawVideoHeight.current = canvasRef.current?.height;
                    drawVideoWidth.current = drawVideoHeight.current * videoAspect;
                    videoOffsetX.current = -(drawVideoWidth.current - canvasRef.current?.width) / 2;
                } else {
                    drawVideoWidth.current = canvasRef.current?.width;
                    drawVideoHeight.current = drawVideoWidth.current / videoAspect;
                    videoOffsetY.current = -(drawVideoHeight.current - canvasRef.current?.height) / 2;
                }

                startDrawingCanvas(previewVideo);
                finalStream = canvasRef.current!.captureStream(15);
            } else {
                finalStream = rawStreamRef.current;
            }

            if (localStream) {
                try {
                    await call?.stopVideo(localStream);
                    const getLocalStream = await localStream.getMediaStream();
                    getLocalStream.getTracks().forEach(track => track.stop?.());
                } catch (e) {
                    console.warn("Erro ao parar localStream antigo", e);
                }
            }

            const newLocalVideoStream = new LocalVideoStream(finalStream);
            setLocalStream(newLocalVideoStream);

            if (videoRef.current) {
                videoRef.current.srcObject = finalStream;
            }
        };

        setup();

        return () => {
            stopStreamsAndAnimation();
        };
    }, [selectedVideoDeviceId, isVideoOn, isAnonymousMode]);

    useEffect(() => {
        if (!call || !localStream) return;

        if (isVideoOn) {
            call.startVideo(localStream).catch(console.error);
        } else {
            call.stopVideo(localStream).catch(console.error);
        }
    }, [call, isVideoOn, localStream]);

    useEffect(() => {
        if (callState === 'Connected') {
            setInMeeting(true);
            onCallStarted();
        }
    }, [callState]);

    const startDrawingCanvas = (video: HTMLVideoElement) => {
        if (drawLoopRef.current) {
            cancelAnimationFrame(drawLoopRef.current);
            drawLoopRef.current = undefined;
        }

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d')!;
        const supportsFilter = typeof ctx.filter !== 'undefined';

        const draw = () => {
            if (!video || video.readyState < 2) {
                drawLoopRef.current = requestAnimationFrame(draw);
                return;
            }

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, videoOffsetX.current, videoOffsetY.current, drawVideoWidth.current, drawVideoHeight.current);

            if (supportsFilter) {
                ctx.filter = 'blur(25px)';
                ctx.drawImage(video, videoOffsetX.current, videoOffsetY.current,drawVideoWidth.current, drawVideoHeight.current);
                ctx.filter = 'none';
            } else {
                ctx.save();

                for (let pass = 0; pass < 3; pass++) {
                    ctx.globalAlpha = 0.02;

                    for (let r = 1; r <= 12; r++) {
                        for (let angle = 0; angle < 360; angle += 10) {
                            const rad = angle * Math.PI / 180;
                            const dx = Math.round(Math.cos(rad) * r);
                            const dy = Math.round(Math.sin(rad) * r);

                            ctx.drawImage(video, videoOffsetX.current + dx, videoOffsetY.current + dy, drawVideoWidth.current, drawVideoHeight.current);
                        }
                    }
                }

                ctx.globalAlpha = 1.0;
                ctx.restore();
            }

            ctx.font = '400 26px Arial';
            ctx.fillStyle = 'white';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(displayName, canvas.width / 2, 20);

            drawLoopRef.current = requestAnimationFrame(draw);
        };

        draw();
    };

    const startCall = async () => {
        if (!callAgent) return;

        try {
            const joinedCall = await callAgent.join({ meetingLink }, {
                audioOptions: { muted: !isAudioOn },
                videoOptions: isVideoOn && localStream ? { localVideoStreams: [localStream] } : undefined,
            });

            setCall(joinedCall);
            setCallState(joinedCall.state);

            joinedCall.on('stateChanged', () => {
                setThreadId(joinedCall.info.threadId);
                setCallState(joinedCall.state);
            });
            joinedCall.on('remoteParticipantsUpdated', () => {
                const updatedParticipants = joinedCall.remoteParticipants;

                updatedParticipants.forEach(participant => {
                    if (!registeredParticipants.current.has(participant)) {
                        participant.on('videoStreamsUpdated', () => {
                            setRemoteParticipants(prev => [...prev]);
                        });
                        registeredParticipants.current.add(participant);
                    }

                    const hasActiveStream = participant.videoStreams.some(s => s.isAvailable);
                    if (hasActiveStream) {
                        setRemoteParticipants(prev => [...prev]);
                    }
                });

                setRemoteParticipants([...updatedParticipants]);
            });
        } catch (err) {
            console.error('Erro ao entrar na chamada:', err);
        }
    };

    const endCall = async () => {
        if (call) {
            setCall(null);
            setVideoOn(false);
            setAudioOn(true);
            setInMeeting(false);
            setShowParticipantsPanel(false);
            setShowChatPanel(false);
            setRemoteParticipants([]);
            onCallEnded();
            await call.hangUp({ forEveryone: false });
        }
        stopStreamsAndAnimation();
        setLocalStream(null);
    };

    const toggleVideo = () => setVideoOn(prev => !prev);

    const toggleAudio = () => {
        setAudioOn(prev => {
            const newState = !prev;
            if (call) newState ? call.unmute() : call.mute();
            return newState;
        });
    };

    const handleDevicesSelected = (audioId: string, videoId: string) => {
        setSelectedAudioDeviceId(audioId);
        setSelectedVideoDeviceId(videoId);
    };

    const toggleChatPanel = () => { setShowParticipantsPanel(false); setShowChatPanel(prev => !prev);  };
    const toggleParticipantsPanel = () => { setShowChatPanel(false); setShowParticipantsPanel(prev => !prev) };

    return (
        <div className={`custom-call-container-wrapper ${showParticipantsPanel ? 'with-panel' : ''}`}>
            <div className={`${!inMeeting ? 'custom-call-container' : 'custom-call-container fullscreen'}`}>
                {deviceError &&
                    <div className="device-error">
                        <Alert
                            warning
                            icon={<ExclamationTriangleIcon outline />}
                            content={deviceError}
                        />
                    </div>
                }

                {(call && inMeeting && queueData.meetingSubject) && (
                    <div className="meeting-title">
                        <Alert
                            content={queueData.meetingSubject}
                            variables={{
                                oof: true,
                            }}
                        />
                    </div>
                )}

                <div className={`${!inMeeting ? 'video-wrapper' : 'video-wrapper-compressed'}`}>
                    <video ref={videoRef} autoPlay muted playsInline />
                    {!isVideoOn && (
                        <div className="video-overlay">
                            {(callState !== 'None' && callState !== 'Connecting' && callState !== 'InLobby' && callState !== 'Connected') &&
                                <>
                                    <CallVideoOffIcon size="large" outline />
                                    <p>Sua câmera está desativada</p>
                                </>
                            }
                            {(callState === 'Connected') &&
                                <div className="video-tile-connected">
                                    <Avatar size="large" name={displayName ?? 'Você'} />
                                    <div className="audio-footer">
                                        {!isAudioOn ? <MicOffIcon size="small" /> : <MicIcon size="small" />}
                                    </div>
                                </div>
                            }
                        </div>
                    )}
                </div>

                {(callState === 'None' || callState === 'Connecting' || callState === 'InLobby') && (
                    <div className="waiting-message">
                        <Loader />
                        <p style={isVideoOn ? { color: "white" } : {}}>Esperando para ser admitido</p>
                    </div>
                )}

                {(callState === 'Connected' && inMeeting) &&
                    <div className="in-meeting">
                        <div className="remote-participants">
                            {remoteParticipants.map((participant, index) => (
                                <RemoteParticipantView key={index} participant={participant} />
                            ))}
                        </div>
                    </div>
                }
            </div>

            <div className={`${!inMeeting ? 'controls' : 'controls fullscreen'}`}>
                <Popup
                    content={
                        <DeviceSelector
                            callClient={callClient}
                            selectedAudioId={selectedAudioDeviceId}
                            selectedVideoId={selectedVideoDeviceId}
                            onDevicesSelected={handleDevicesSelected}
                            deviceError={deviceError}
                        />
                    }
                    trigger={<Button className="settings-button" onClick={openSettings} icon={<SettingsIcon />} text content="" />}
                    pointing
                />

                {inMeeting ?
                    <>
                        <Tooltip
                            trigger={<Button disabledFocusable={!!deviceError} onClick={toggleAudio} icon={isAudioOn ? <MicIcon /> : <MicOffIcon />} text content="Microfone" />}
                            content={!deviceError ? (isAudioOn ? "Desabilitar microfone" : "Habilitar microfone") : "Microfone bloqueado"}
                        />

                        <Tooltip
                            trigger={<Button disabledFocusable={!!deviceError} onClick={toggleVideo} icon={isVideoOn ? <CallVideoIcon /> : <CallVideoOffIcon />} text content="Câmera" />}
                            content={!deviceError ? (isVideoOn ? "Desabilitar câmera" : "Habilitar câmera") : "Câmera bloqueada"}
                        />
                    </>
                    :
                    <>
                        <Button onClick={toggleAudio} disabled={!!deviceError} icon={isAudioOn ? <MicIcon /> : <MicOffIcon />} text content="Microfone" />
                        <Button onClick={toggleVideo} disabled={!!deviceError} icon={isVideoOn ? <CallVideoIcon /> : <CallVideoOffIcon />} text content="Câmera" />
                    </>
                }

                {(!inMeeting && isVideoOn) &&
                    <Checkbox checked={isAnonymousMode} onClick={toggleAnonymousMode} label="Anonimizado" toggle />
                }

                {(inMeeting && call && threadId) &&
                    <Button onClick={toggleChatPanel} icon={<ChatIcon />} text content="Chat" />    
                }

                {(inMeeting && call) && (
                    <Button onClick={toggleParticipantsPanel} icon={<UserFriendsIcon />} text content="Participantes" />
                )}

                {!call ? (
                    <Button className="start-call" onClick={startCall} icon={<CallIcon />} text content="Entrar na chamada" />
                ) : (
                    <Button className="end-call" onClick={endCall} icon={<CallEndIcon />} text content="Encerrar" />
                )}
            </div>

            {(inMeeting && showParticipantsPanel) && (
                <Participants
                    toggleParticipantsPanel={toggleParticipantsPanel}
                    currentUserName={displayName}
                    isAudioOn={isAudioOn}
                    remoteParticipants={remoteParticipants}
                />
            )}

            {(inMeeting && showChatPanel && threadId) && (
                <CustomChat
                    token={token}
                    userId={userId}
                    displayName={displayName}
                    threadId={threadId}
                    endpointUrl={endpoint}
                    onClose={toggleChatPanel}
                />
            )}
        </div>
    );
};

export default React.memo(CustomCallScreen);
